import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App from './App';
import { preloadRoute } from './routeRegistry';
import './style.css';

const rootElement = document.getElementById('root')!;

const PRELOAD_TIMEOUT_MS = 2000;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(
      () => reject(new Error(`preload timeout ${ms}ms`)),
      ms,
    );
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

async function preloadCurrentRoute() {
  try {
    await withTimeout(
      preloadRoute(window.location.pathname),
      PRELOAD_TIMEOUT_MS,
    );
  } catch (err) {
    console.warn('[boot] preload skipped:', (err as Error).message);
  }
}

async function boot() {
  // If root already has children (pre-rendered HTML), hydrate instead of render.
  // The current route's chunk must be loaded BEFORE hydration so the page
  // component renders synchronously and matches the pre-rendered HTML.
  // Se il preload resta appeso oltre PRELOAD_TIMEOUT_MS (CDN lento, chunk
  // temporaneamente 404 durante un deploy), idratiamo comunque: LazyPage
  // mostrerà il placeholder aria-busy invece di bloccare a tempo indefinito.
  const currentPath = window.location.pathname.replace(/\/$/, '') || '/';
  const snapshotPath = document.documentElement.dataset.prerenderRoute;
  if (rootElement.hasChildNodes() && snapshotPath === currentPath) {
    await preloadCurrentRoute();
    hydrateRoot(
      rootElement,
      <StrictMode>
        <App />
      </StrictMode>,
    );
  } else {
    // Nessuna idratazione possibile (URL non prerenderizzato, 404.html):
    // precarichiamo comunque il chunk, altrimenti il primo render mostra il
    // placeholder di LazyPage al posto della pagina.
    await preloadCurrentRoute();
    createRoot(rootElement).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  }
}

boot().catch((err) => {
  // Impedisce che un'eccezione in fase di preload/hydrate diventi
  // un'unhandled promise rejection: la logghiamo per la telemetria.
  console.error('[boot] fallito:', err);
});
